import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { ServiceObject } from '../../../@types/service.type';
import type { Tag } from '../../../@types/tag.type';
import { SummaryBar } from '../../../components/molecules/SummaryBar';
import type { AutocompleteTagType } from '../../../components/organisms/Tags';

export type ServiceDetailTabKey = 'details' | 'administration' | 'learn';

export interface ServiceDetailHeaderProps {
  serviceId: number | string;
  service: ServiceObject | null;
  clientLogoPic?: string;
  activeTab: ServiceDetailTabKey;
  onTabChange: (tab: ServiceDetailTabKey) => void;
  /** When false, the Administration tab button is not rendered. */
  showAdministrationTab: boolean;
  /** When false, the Other tab button is not rendered. */
  showOtherTab: boolean;
  /** When true, the Technician allocation field is suppressed in the SummaryBar. */
  hideAllocation?: boolean;
  /** Tags available to assign, used by the SummaryBar's inline tag editor. */
  allTags?: Tag[];
  /** Omit to keep the tag chips read-only. */
  onSaveTags?: (args: {
    selectedTags: (AutocompleteTagType | string)[];
    newTags: Tag[];
  }) => Promise<void>;
  onStatusChange?: () => void;
  onAllocate?: () => void;
  onPartAction?: () => void;
  onTask?: () => void;
  onCancelFutile?: () => void;
  onReopen?: () => void;
  /** Tooltip text for the Status button when it should be rendered disabled. */
  statusDisabledReason?: string;
}

const tabSx = (active: boolean) => ({
  px: 2,
  py: 1,
  fontSize: '13px',
  fontWeight: active ? 600 : 500,
  color: active ? 'v4.accent' : 'text.secondary',
  borderBottom: active ? '2px solid' : '2px solid transparent',
  borderBottomColor: active ? 'v4.accent' : 'transparent',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  fontFamily: 'inherit',
  mb: '-2px',
});

/**
 * Renders the breadcrumb, sticky summary bar and tab strip that sit above
 * the active tab content on the service detail page. Extracted from
 * ServiceDetail so the parent stays under the 250-line cap and the tab
 * visibility rules live in one place.
 */
export const ServiceDetailHeader: React.FC<ServiceDetailHeaderProps> = ({
  serviceId,
  service,
  clientLogoPic,
  activeTab,
  onTabChange,
  showAdministrationTab,
  showOtherTab,
  hideAllocation = false,
  allTags,
  onSaveTags,
  onStatusChange,
  onAllocate,
  onPartAction,
  onTask,
  onCancelFutile,
  onReopen,
  statusDisabledReason,
}) => (
  <>
    <Box
      sx={{
        px: 2.5,
        py: 1.25,
        fontSize: '13px',
        color: 'text.secondary',
        display: 'flex',
        gap: 0.75,
        '@media print': { display: 'none' },
      }}
    >
      <MuiLink component={RouterLink} to="/services" underline="none" sx={{ color: 'v4.accent' }}>
        Jobs
      </MuiLink>
      <span>/</span>
      <span>JOB-{serviceId}</span>
    </Box>

    <Box
      sx={(theme) => ({
        position: 'sticky',
        top: 0,
        zIndex: 50,
        bgcolor: 'background.default',
        px: 2.5,
        py: 1,
        boxShadow: theme.shadows[2],
      })}
    >
      <SummaryBar
        service={service}
        logoPic={clientLogoPic}
        hideAllocation={hideAllocation}
        allTags={allTags}
        onSaveTags={onSaveTags}
        onStatusChange={onStatusChange}
        onAllocate={onAllocate}
        onPartAction={onPartAction}
        onTask={onTask}
        onCancelFutile={onCancelFutile}
        onReopen={onReopen}
        statusDisabledReason={statusDisabledReason}
      />
    </Box>

    <Box sx={{ mx: 2.5, '@media print': { display: 'none' } }}>
      <Box
        role="tablist"
        aria-label="Service detail tabs"
        sx={{
          display: 'flex',
          gap: 0,
          borderBottom: '2px solid',
          borderColor: 'v4.borderDark',
        }}
      >
        <Box
          component="button"
          role="tab"
          aria-selected={activeTab === 'details'}
          sx={tabSx(activeTab === 'details')}
          onClick={() => onTabChange('details')}
        >
          Details
        </Box>
        {showAdministrationTab && (
          <Box
            component="button"
            role="tab"
            aria-selected={activeTab === 'administration'}
            sx={tabSx(activeTab === 'administration')}
            onClick={() => onTabChange('administration')}
          >
            Administration
          </Box>
        )}
        {showOtherTab && (
          <Box
            component="button"
            role="tab"
            aria-selected={activeTab === 'learn'}
            sx={tabSx(activeTab === 'learn')}
            onClick={() => onTabChange('learn')}
          >
            Other
          </Box>
        )}
      </Box>
    </Box>
  </>
);
